import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCrown, faCircleXmark, faClock } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../AuthContext';
import PremiumLayout from './PremiumLayout';
import PremiumSteps from './PremiumSteps';
import { formatRoDate } from './formatRoDate';

const apiUrl = process.env.REACT_APP_API_URL;
const POLL_MS = 2000;
const MAX_TRIES = 15;

/**
 * Ajungem aici fie direct din checkout (state cu status 'paid'), fie prin return_url
 * după 3D Secure / plată în procesare (?order=ID). În al doilea caz întrebăm serverul,
 * care verifică la Stripe, până când plata e decisă.
 */
const PremiumSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const authRef = useRef(auth);
  authRef.current = auth;

  const params = new URLSearchParams(location.search);
  const orderId = location.state?.orderId || params.get('order');
  const stateStatus = location.state?.status;
  const hasContext = Boolean(orderId || stateStatus === 'paid');
  const [status, setStatus] = useState(stateStatus === 'paid' ? 'paid' : 'checking');
  const [expiresAt, setExpiresAt] = useState(location.state?.expiresAt || auth?.user?.premium_expires_at || null);
  const planName = location.state?.planName || 'ContaLearn Premium';

  // Reimprospateaza userul din context (is_premium) la mount si la fiecare schimbare de status (ex. 'paid').
  useEffect(() => {
    if (hasContext && authRef.current?.checkAuth) authRef.current.checkAuth();
  }, [status, hasContext]);

  useEffect(() => {
    if (status !== 'checking' || !orderId) return undefined;
    let cancelled = false;
    let tries = 0;
    let timer = null;

    const tick = async () => {
      tries += 1;
      try {
        const res = await fetch(`${apiUrl}/premium/orders/${orderId}/sync`, { method: 'POST', credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data.status === 'paid') {
          setExpiresAt(data.premium_expires_at);
          setStatus('paid');
          return;
        }
        if ((res.ok && data.status === 'failed') || res.status === 404) {
          setStatus('failed');
          return;
        }
      } catch (_) {
        // rețea: reîncercăm
      }
      if (tries >= MAX_TRIES) {
        setStatus('timeout');
        return;
      }
      timer = setTimeout(tick, POLL_MS);
    };

    timer = setTimeout(tick, 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [status, orderId]);

  if (!hasContext) return <Navigate to="/premium" replace />;

  if (status === 'checking') {
    return (
      <PremiumLayout title="Se confirmă plata…" subtitle="Durează câteva secunde. Nu închide pagina.">
        <PremiumSteps current={2} />
        <section className="pm-card pm-center" aria-busy="true">
          <div className="pm-success-badge pm-success-badge--pending">
            <FontAwesomeIcon icon={faClock} />
          </div>
          <p className="pm-note">Verificăm plata la procesatorul de plăți și activăm accesul Premium.</p>
        </section>
      </PremiumLayout>
    );
  }

  if (status === 'failed') {
    return (
      <PremiumLayout title="Plata nu a reușit" subtitle="Nu s-a încasat nimic. Poți încerca din nou cu alt card.">
        <PremiumSteps current={1} />
        <section className="pm-card pm-center">
          <div className="pm-success-badge pm-success-badge--failed">
            <FontAwesomeIcon icon={faCircleXmark} />
          </div>
          <div className="pm-actions">
            <button type="button" className="pm-btn pm-btn-primary pm-btn-block" onClick={() => navigate('/premium/checkout')}>
              Încearcă din nou
            </button>
            <button type="button" className="pm-btn pm-btn-block" onClick={() => navigate('/premium')}>
              Înapoi la ofertă
            </button>
          </div>
        </section>
      </PremiumLayout>
    );
  }

  if (status === 'timeout') {
    return (
      <PremiumLayout title="Plata este în curs de procesare" subtitle="Confirmarea întârzie. Accesul se activează automat când plata este confirmată.">
        <PremiumSteps current={2} />
        <section className="pm-card pm-center">
          <div className="pm-success-badge pm-success-badge--pending">
            <FontAwesomeIcon icon={faClock} />
          </div>
          <div className="pm-actions">
            <button type="button" className="pm-btn pm-btn-teal pm-btn-block" onClick={() => setStatus('checking')}>
              Verifică din nou
            </button>
            <button type="button" className="pm-btn pm-btn-block" onClick={async () => {
              if (authRef.current?.checkAuth) await authRef.current.checkAuth();
              navigate('/functionalpagelogin');
            }}>
              Înapoi la cursuri
            </button>
          </div>
        </section>
      </PremiumLayout>
    );
  }

  return (
    <PremiumLayout title="Plata reușită" subtitle="Mulțumim! Contul tău este acum Premium.">
      <PremiumSteps current={2} />
      <section className="pm-card pm-center">
        <div className="pm-success-badge">
          <FontAwesomeIcon icon={faCircleCheck} />
        </div>
        <span className="pm-plan-badge">
          <FontAwesomeIcon icon={faCrown} /> {planName}
        </span>
        {expiresAt && (
          <p className="pm-note" style={{ marginTop: 14 }}>
            Acces Premium activ până la <strong>{formatRoDate(expiresAt)}</strong>.
          </p>
        )}
        <div className="pm-actions">
          <button type="button" className="pm-btn pm-btn-teal pm-btn-block" onClick={() => navigate('/functionalpagelogin')}>
            Înapoi la cursuri
          </button>
        </div>
      </section>
    </PremiumLayout>
  );
};

export default PremiumSuccess;
